import { useMemo } from "react";

import { any } from "psims/lib/collections";
import { is } from "psims/lib/type-assertions";
import { recordActionAsEnum } from "psims/models/api/data-submission-record-action";
import NotificationMessage, { NotificationPart } from "../../shared/notification-message";
import { UseSubmit } from "../../shared/use-submit";
import { UseMsoComment } from "../shared/mso-comment/use-mso-comment";
import { UseMsoStockholdingStorages } from "../shared/mso-stockholding-storages/use-mso-stockholding-storages";
import { UseRefinerAnnualActivities } from "./annual-activities/use-refiner-annual-activities";
import { UseRefinerAnnualActivityNavigation } from "./use-refiner-annual-activity-navigation";
import { UseRefinerAnnualActivityProgress } from "./use-refiner-annual-activity-progress";
import { UseImporterImports } from "../shared/imports/use-importer-imports";
import useEnforceableArrangements from "../shared/enforceable-arrangements/use-enforceable-arrangements";
import useAustralianControllingCorporations from "../shared/australian-controlling-corporations/use-australian-controlling-corporations";

interface UseRefinerAnnualActivityNotificationsProps {
  annualActivitiesCtrl: UseRefinerAnnualActivities;
  commentsCtrl: UseMsoComment;
  importsCtrl: UseImporterImports;
  navCtrl: UseRefinerAnnualActivityNavigation;
  progressCtrl: UseRefinerAnnualActivityProgress;
  serviceMessages: Array<string>;
  storageSitesCtrl: UseMsoStockholdingStorages;
  enforceableArrangementsCtrl: ReturnType<typeof useEnforceableArrangements>;
  australianControllingCorporationsCtrl: ReturnType<typeof useAustralianControllingCorporations>;
  submitCtrl: UseSubmit;
}

function useRefinerAnnualActivityNotifications({
  annualActivitiesCtrl,
  commentsCtrl,
  importsCtrl,
  navCtrl,
  progressCtrl,
  serviceMessages,
  storageSitesCtrl,
  enforceableArrangementsCtrl,
  australianControllingCorporationsCtrl,
  submitCtrl,
}: UseRefinerAnnualActivityNotificationsProps) {

  const validationNotifications = useMemo(() => {
    const currentIndex = progressCtrl.currentStep.index;

    // ── Step 0: Imports + Annual Activities ──────────────────────────────
    if (currentIndex === 0) {
      return [
        // Imports
        ...(importsCtrl.updateImports
          .filter(a => a.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s =>
            Object.entries(s.validations)
              .filter(([k, v]) => v.notification.parts != null)
              .filter(([k, v]) => v.validationResult.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

        // Annual activities
        ...(annualActivitiesCtrl.updateAnnualActivities
          .filter(a => a.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s =>
            Object.entries(s.validations)
              .filter(([k, v]) => v.notification.parts != null)
              .filter(([k, v]) => v.validationResult.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

        // Comments
        ...(commentsCtrl.data.recordAction === recordActionAsEnum('Delete') ? [] : [
          ...commentsCtrl.validations
            .filter(v => v.notification.parts != null)
            .map(v => v.notification.parts)
            .filter(is)
            .map(parts => ({ content: NotificationMessage({ parts }) }))
        ]),
      ];
    }

    if (currentIndex === 1) {
        return []; // Production — no notifications yet
    }
    // ── Step 1: Storage ──────────────────────────────────────────────────
    if (currentIndex === 2) {
      return [
        ...(storageSitesCtrl.updateStorages
          .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s => {
            const d = (s.validations as Record<string, any>)?.delete;
            if (d == null) return [];
            return [d.notification].filter(is);
          })
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

        ...(storageSitesCtrl.updateStorages
          .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s =>
            Object.entries(s.validations as Record<string, any>)
              .filter(([k, v]) => v?.notification?.parts != null)
              .filter(([k, v]) => v.validationResult?.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
      ];
    }

    // ── Step 2: Enforceable Arrangements ────────────────────────────────
    if (currentIndex === 3) {
      return [
        ...(enforceableArrangementsCtrl.rows
          .map(s =>
            Object.entries(s.validations as Record<string, any>)
              .filter(([k, v]) => v?.notification?.parts != null)
              .filter(([k, v]) => v.validationResult?.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
      ];
    }

    // ── Step 3: Australian Controlling Corporations ──────────────────────
    if (currentIndex === 4) {
      return [
        ...(australianControllingCorporationsCtrl.rows
          .map(s =>
            Object.entries(s.validations as Record<string, any>)
              .filter(([k, v]) => v?.notification?.parts != null)
              .filter(([k, v]) => v.validationResult?.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
      ];
    }

    // ── Submit page (index 4) ────────────────────────────────────────────
    return [
      ...submitCtrl.notifications,

      ...(annualActivitiesCtrl.updateAnnualActivities
        .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
        .map(s => {
          const { delete: d } = s.validations;
          if (d == null) return [];
          return [d.notification].filter(is);
        })
        .flat() as Array<{ parts: Array<NotificationPart> }>)
        .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

      ...(storageSitesCtrl.updateStorages
        .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
        .map(s => {
          const d = (s.validations as Record<string, any>)?.delete;
          if (d == null) return [];
          return [d.notification].filter(is);
        })
        .flat() as Array<{ parts: Array<NotificationPart> }>)
        .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

      ...(importsCtrl.updateImports
        .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
        .map(s => {
          const { delete: d } = s.validations;
          if (d == null) return [];
          return [d.notification].filter(is);
        })
        .flat() as Array<{ parts: Array<NotificationPart> }>)
        .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
    ];

  }, [
    annualActivitiesCtrl.updateAnnualActivities,
    commentsCtrl.data,
    commentsCtrl.validations,
    importsCtrl.updateImports,
    navCtrl.lastSaveAttemptIndex,
    progressCtrl.currentStep.index,
    storageSitesCtrl.updateStorages,
    enforceableArrangementsCtrl.rows,
    australianControllingCorporationsCtrl.rows,
    submitCtrl,
  ]);

  const infoNotifications = useMemo(() => {
    const messages: Array<{ content: JSX.Element }> = [];

    if (
      progressCtrl.currentStep.index === 0 &&
      any(
        annualActivitiesCtrl.updateAnnualActivities
          .map(a => a.data)
          .filter(a => a.recordAction !== recordActionAsEnum('Delete')),
        a => a.enforceableArrangements === true
      )
    ) {
      messages.push({
        content: NotificationMessage({
          parts: [
            <span key={1}>You have selected 'Enforceable arrangements'. Please provide a copy of the legally enforceable arrangement/s to the department by uploading it to the Documents page under the Manage company menu.</span>
          ]
        })
      });
    }

    return [
      ...(progressCtrl.currentStep.index === 0
        ? annualActivitiesCtrl.updateAnnualActivities.map(a => [
            ...(a.validationAlerts.totalImportVolumeOnBehalf?.notification.parts == null ? [] : [{ content: NotificationMessage({ parts: a.validationAlerts.totalImportVolumeOnBehalf.notification.parts }) }]),
            ...(a.validationAlerts.totalImportVolumeOthers?.notification.parts == null    ? [] : [{ content: NotificationMessage({ parts: a.validationAlerts.totalImportVolumeOthers.notification.parts }) }]),
            ...(a.validationAlerts.totalAnnualVolume?.notification.parts == null          ? [] : [{ content: NotificationMessage({ parts: a.validationAlerts.totalAnnualVolume.notification.parts }) }]),
          ])
        : []
      ),
      ...messages,
    ].flat();
  }, [annualActivitiesCtrl.updateAnnualActivities, progressCtrl.currentStep.index]);

  const serviceNotifications = useMemo(() => {
    return serviceMessages.map(message => ({
      content: NotificationMessage({ parts: [message] })
    }));
  }, [serviceMessages]);

  return {
    infoNotifications,
    serviceNotifications,
    validationNotifications,
  };
}

export default useRefinerAnnualActivityNotifications;

export type UseRefinerAnnualActivityNotifications = ReturnType<typeof useRefinerAnnualActivityNotifications>;